import React from 'react';
import { OmniIconComponent } from '../';

/**
 * OverBudgetWarningModal - Reusable modal component for displaying over-budget warnings
 * 
 * Displays a warning dialog when operations exceed planned budgets, showing:
 * - List of over-budget items with amounts
 * - Customizable warning messages
 * - Cancel and Continue actions
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when user cancels or closes modal
 * @param {Function} props.onContinue - Callback when user confirms to proceed despite warnings
 * @param {Array<Object>} props.overBudgetItems - Items exceeding budget [{name: string, overAmount: number}]
 * @param {string} props.localCurrency - Local currency code (e.g., 'EUR', 'GBP')
 * @param {boolean} props.isLocal - Whether to use local currency or default to USD
 * @param {string} [props.warningMessage] - Custom warning message (optional)
 * @param {string} [props.warningFooter] - Custom footer message (optional)
 * @param {string} [props.continueButtonText] - Custom continue button text (optional)
 * @param {string} [props.title] - Custom modal title (optional, defaults to "Over Budget")
 */
const OverBudgetWarningModal = ({
    isOpen,
    onClose,
    onContinue,
    overBudgetItems = [],
    localCurrency,
    isLocal,
    warningMessage = "You're about to perform an action that exceeds the planned media budget in Planit.",
    warningFooter = "Are you sure you want to continue? If so, please connect with your planning team to make sure budgets are aligned between Planit and Media Ops Hub.",
    continueButtonText = "Yes, continue with budget changes",
    title = "Over Budget"
}) => {
    if (!isOpen) return null;

    const currency = isLocal && localCurrency ? localCurrency : 'USD';

    const formatCurrency = (amount) => {
        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div className="create-media-order-wizard-overlay" style={{ zIndex: 1001 }}>
            <div className="budget-warning-modal">
                <div className="budget-warning-header">
                    <OmniIconComponent 
                        icon-id="omni:informative:alert" 
                        style={{ fontSize: '24px', color: '#C87619' }}
                    />
                    <h3>{title}</h3>
                    <button 
                        className="tertiary" 
                        onClick={onClose}
                        style={{ marginLeft: 'auto' }}
                    >
                        <OmniIconComponent 
                            icon-id="omni:interactive:close" 
                            style={{ fontSize: '24px' }} 
                        />
                    </button>
                </div>

                <div className="budget-warning-content">
                    <p className="warning-message">
                        {warningMessage}
                    </p>

                    <div className="over-budget-list">
                        {overBudgetItems.map((item, index) => (
                            <div key={index} className="over-budget-item">
                                <span className="item-name">{item.name}:</span>
                                <span className="item-amount">
                                    {formatCurrency(item.overAmount)} over budget
                                </span>
                            </div>
                        ))}
                    </div>

                    <p className="warning-footer">
                        {warningFooter}
                    </p>
                </div>

                <div className="budget-warning-actions">
                    <button 
                        className="tertiary" 
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button 
                        className="primary" 
                        onClick={onContinue}
                    >
                        {continueButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OverBudgetWarningModal;
